import { Checkbox, DialogProps, FormControlLabel } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "components/Alert";
import Button from "components/Button";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "components/Dialog";
import TextField from "components/TextField";
import { groupChatKey, groupChatMessagesKey, groupChatsListKey } from "features/queryKeys";
import { Empty } from "google-protobuf/google/protobuf/empty_pb";
import { RpcError } from "grpc-web";
import { useTranslation } from "i18n";
import { GLOBAL, MESSAGES } from "i18n/namespaces";
import { GroupChat } from "proto/conversations_pb";
import React from "react";
import { useForm } from "react-hook-form";
import { service } from "service";

interface GroupChatSettingsData {
  title: string;
  onlyAdminsInvite: boolean;
}

export default function GroupChatSettingsDialog({
  groupChat,
  ...props
}: DialogProps & { groupChat: GroupChat.AsObject }) {
  const { t } = useTranslation([GLOBAL, MESSAGES]);
  const { register, handleSubmit } = useForm<GroupChatSettingsData>();

  const queryClient = useQueryClient();
  const mutation = useMutation<Empty, RpcError, GroupChatSettingsData>({
    mutationFn: ({ title, onlyAdminsInvite }) =>
      service.conversations.editGroupChat(groupChat.groupChatId, title, onlyAdminsInvite),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [groupChatMessagesKey(groupChat.groupChatId)],
      });
      queryClient.invalidateQueries({
        queryKey: [groupChatsListKey],
      });
      queryClient.invalidateQueries({
        queryKey: [groupChatKey(groupChat.groupChatId)],
      });
      if (props.onClose) props.onClose({}, "escapeKeyDown");
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutation.mutate(data);
  });

  return (
    <Dialog {...props} aria-labelledby="group-chat-settings-dialog-title">
      <DialogTitle id="group-chat-settings-dialog-title">{t("messages:group_chat_settings_dialog.title")}</DialogTitle>
      <DialogContent>
        <form onSubmit={onSubmit}>
          {mutation.error && <Alert severity={"error"}>{mutation.error?.message}</Alert>}
          <TextField
            id="group-chat-settings-chat-title"
            {...register("title")}
            defaultValue={groupChat.title}
            label={t("messages:group_chat_settings_dialog.chat_title.field_label")}
          />
          <FormControlLabel
            control={<Checkbox {...register("onlyAdminsInvite")} defaultChecked={groupChat.onlyAdminsInvite} />}
            label={t("messages:group_chat_settings_dialog.only_admins_invite.field_label")}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSubmit} loading={mutation.isPending}>
          {t("global:save")}
        </Button>
        <Button onClick={() => (props.onClose ? props.onClose({}, "escapeKeyDown") : null)}>
          {t("global:cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
